"use client";

import * as React from "react";
import { Moon, Sun, Monitor } from "lucide-react";
import { useTheme } from "next-themes";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function ThemeToggle() {
  const { setTheme, theme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  // After mounting, we can show the toggle component
  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  // Show active theme in button aria-label
  const currentTheme = theme || "system";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 px-0 text-palette-gold-dark dark:text-palette-cyan-dark transition-colors"
          aria-label={`Current theme: ${currentTheme}`}
        >
          <Sun className="rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="bottom"
        sideOffset={4}
        className="hover-fix"
      >
        <style jsx global>{`
          /* Only apply in light mode */
          :root:not(.dark) .hover-fix [role="menuitem"]:hover {
            color: #000000 !important;
          }
          /* Ensure dark mode uses white text */
          :root.dark .hover-fix [role="menuitem"]:hover {
            color: #ffffff !important;
          }
        `}</style>
        <DropdownMenuItem
          onClick={() => setTheme("light")}
          className={
            currentTheme === "light"
              ? "bg-palette-green-dark text-white font-semibold"
              : undefined
          }
        >
          <Sun
            className={`h-4 w-4 mr-2 ${
              currentTheme === "light"
                ? "text-white"
                : "text-palette-gold-dark dark:text-yellow-300"
            }`}
          />
          <span>{currentTheme === "light" ? "Light ✓" : "Light"}</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => setTheme("dark")}
          className={
            currentTheme === "dark"
              ? "bg-palette-cyan-dark text-white font-semibold"
              : undefined
          }
        >
          <Moon
            className={`h-4 w-4 mr-2 ${
              currentTheme === "dark"
                ? "text-white"
                : "text-palette-cyan-dark dark:text-cyan-300"
            }`}
          />
          <span>{currentTheme === "dark" ? "Dark ✓" : "Dark"}</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => setTheme("system")}
          className={
            currentTheme === "system"
              ? "bg-palette-yellow-dark text-black font-semibold"
              : undefined
          }
        >
          <Monitor
            className={`h-4 w-4 mr-2 ${
              currentTheme === "system"
                ? "text-black"
                : "text-black dark:text-yellow-300"
            }`}
          />
          <span>{currentTheme === "system" ? "System ✓" : "System"}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
